import { Spine } from "@pixi/spine-pixi";
import * as PIXI from "pixi.js";
import { GameEngine } from "./server/GameEngine";
import { delay } from "src/shared/util/UtilFunctions";
import { ParticleSystem } from "./Particle";
import { GameAssets } from "src/shared/util/GameAssets";
import { soundManager } from "./shared/UI/SoundManager";
import { gameStateSoundManager } from "./GameStateSoundManager";
import { SoundInstance } from "./shared/UI/SoundManagerUtils";

const WIN_FONT_SIZE = 30;
const BIG_WIN_FONT_SIZE = 40;
const LABEL_OFFSET_BIG_WINS: number = 250;
const WIN_REMAIN_DURATION: number = 1500;
const WIN_LAST_DURATION: number[] = [6000, 10000, 18000]; // a few seconds off each duration, to line up with the sounds

export class WinCounter extends PIXI.Container {
    private winLabel: PIXI.BitmapText;
    private winLabelContainer: PIXI.Container;
    private prize: number = 0;
    private odd: number = 0;
    private winTitles!: Spine;
    private winLayer: PIXI.Graphics;
    private isStopped: boolean = false;
    private level: number = -1;
    private particleSystem: ParticleSystem;
    // Used to prevent stopping and replaying the same sounds
    private alreadyPlayedWinSounds: Set<string> = new Set();

    private winSmall: SoundInstance | undefined = new SoundInstance();
    private winNormal: SoundInstance | undefined = new SoundInstance();
    private winBig: SoundInstance | undefined = new SoundInstance();
    private winSuper: SoundInstance | undefined = new SoundInstance();
    private winMega: SoundInstance | undefined = new SoundInstance();
    private swordBattle: SoundInstance | undefined = new SoundInstance();
    private lionBattle: SoundInstance | undefined = new SoundInstance();

    constructor() {
        super();
        this.particleSystem = new ParticleSystem(
            GameAssets.textures.coinAtlas.animations.coin,
            100,
        );
        this.winLabel = new PIXI.BitmapText({
            text: "123456.00",
            style: {
                fontSize: WIN_FONT_SIZE,
                fontFamily: "winFont",
                fill: "0xffffff",
            },
        });

        this.winLabelContainer = new PIXI.Container();

        this.winLabel.position.set(0, -25);
        this.winLabel.anchor.set(0.5);

        this.winLayer = new PIXI.Graphics().rect(-2000, -2000, 4000, 4000).fill(0);
        this.winLayer.alpha = 0.7;

        this.winTitles = Spine.from({
            skeleton: "winsJson",
            atlas: "winsAtlas",
        });

        this.winTitles.state.addListener({
            event: function(entry, event) { },
            start: async (entry) => {
                if (this.isStopped && entry.animation!.name.includes("Hide")) {
                    switch (this.level) {
                        case 2:
                            await soundManager.playSound("winBigEnd");
                            break;
                        case 3:
                            await soundManager.playSound("winSuperEnd");
                            break;
                        case 4:
                            await soundManager.playSound("winMegaEnd");
                            break;
                    }
                }
            },
            complete: async (entry) => {
                if (entry.animation) {
                    this.emit(entry.animation.name);
                }

                if (entry.animation!.name.includes("Show")) {
                    console.log(entry.animation!.name);
                    switch (this.level) {
                        case 2:
                            await soundManager.playSound("swordHit");
                            break;
                        case 3:
                            if (entry.animation!.name.includes("big")) {
                                await soundManager.playSound("swordHit");
                            } else if (entry.animation!.name.includes("super")) {
                                this.swordBattle = await soundManager.playSound("swordBattle");
                            }
                            break;
                        case 4:
                            if (entry.animation!.name.includes("big")) {
                                await soundManager.playSound("swordHit");
                            } else if (entry.animation!.name.includes("super")) {
                                this.swordBattle = await soundManager.playSound("swordBattle");
                            } else if (entry.animation!.name.includes("mega")) {
                                this.lionBattle = await soundManager.playSound("lionBattle");
                            }
                            break;
                    }
                }

                // Handle quick stop sounds
                if (this.isStopped && entry.animation!.name.includes("Hide")) {
                    console.log("COMPLETED ANIMATION WHILE IS STOPPED IS TRUE");
                    switch (this.level) {
                        case 2:
                            if (this.winBig) {
                                soundManager.stopSoundInstance(this.winBig);
                                this.winBig = undefined;
                            }
                            break;
                        case 3:
                            if (this.winSuper) {
                                soundManager.stopSoundInstance(this.winSuper);
                                this.winSuper = undefined;
                            }
                            break;
                        case 4:
                            if (this.winMega) {
                                soundManager.stopSoundInstance(this.winMega);
                                this.winMega = undefined;
                            }
                            break;
                    }
                }
            },
            end: function(entry) { },
            dispose: function(entry) { },
        });
        this.winTitles.position.set(0, -200);
        this.winTitles.scale.set(0.5);

        this.addChild(this.winLayer);
        this.addChild(this.particleSystem);
        this.addChild(this.winTitles);
        this.addChild(this.winLabelContainer);
        this.winLayer.visible = false;
        this.winTitles.visible = false;
        this.winLabelContainer.visible = false;

        this.winLabelContainer.addChild(this.winLabel);
    }

    private async _showStaticWin() {
        return new Promise((resolve) => {
            console.log("static win");
            this.winLabel.y = 0;
            this.winLabel.style.fontSize = WIN_FONT_SIZE;
            this.winLabel.text = GameEngine.getInstance()
                .getFormatter()
                .format(this.prize);

            setTimeout(() => {
                if (this.winSmall) {
                    soundManager.stopSoundInstance(this.winSmall);
                    this.winSmall = undefined;
                }

                resolve();
            }, 1000);
        });
    }

    private async _showCounterWin() {
        return new Promise(async (resolve) => {
            console.log("counter win");
            this.winLabel.y = 0;
            this.winLabel.style.fontSize = BIG_WIN_FONT_SIZE;
            this.winLabel.text = GameEngine.getInstance()
                .getFormatter()
                .format(this.prize);

            await this._showProgressWin(3000);
            setTimeout(() => {
                if (this.winNormal) {
                    soundManager.stopSoundInstance(this.winNormal);
                    this.winNormal = undefined;
                }

                resolve();
            }, 1000);
        });
    }

    private async _showBigWin() {
        return new Promise<void>(async (resolve) => {
            this.particleSystem.start(0, 250, 100);
            this.winLabel.y = LABEL_OFFSET_BIG_WINS;
            this.winTitles.visible = true;
            this.winTitles.state.setAnimation(0, `bigWinShow`, false);
            this.winTitles.state.addAnimation(0, `bigWinLoop`, true);
            this.winLabel.style.fontSize = BIG_WIN_FONT_SIZE;

            await this._showProgressWin(WIN_LAST_DURATION[0]);
            await delay(WIN_REMAIN_DURATION);
            this.winTitles.state.setAnimation(0, `bigWinHide`, false);

            this.particleSystem.stop();
            setTimeout(() => {
                resolve();
            }, 500);
        });
    }

    private async _showSuperWin() {
        return new Promise<void>(async (resolve) => {
            this.particleSystem.start(0, 250, 100);
            this.winLabel.y = LABEL_OFFSET_BIG_WINS;
            this.winTitles.visible = true;
            this.winTitles.state.setAnimation(0, `bigWinShow`, false);
            this.winTitles.state.addAnimation(0, `bigWinLoop`, false);
            this.winTitles.state.addAnimation(0, `bigWinHide`, false);
            this.winTitles.state.addAnimation(0, `superWinShow`, false);
            this.winTitles.state.addAnimation(0, `superWinLoop`, true);
            this.winLabel.style.fontSize = BIG_WIN_FONT_SIZE;
            await this._showProgressWin(WIN_LAST_DURATION[1]);
            await delay(WIN_REMAIN_DURATION);
            this.winTitles.state.setAnimation(0, `superWinHide`, false);

            if (this.swordBattle) {
                soundManager.stopSoundInstance(this.swordBattle);
                this.swordBattle = undefined;
            }

            this.particleSystem.stop();
            setTimeout(() => {
                resolve();
            }, 500);
        });
    }

    private async _showMegaWin() {
        return new Promise<void>(async (resolve) => {
            this.particleSystem.start(0, 250, 100);
            this.winLabel.y = LABEL_OFFSET_BIG_WINS;
            this.winTitles.visible = true;
            this.winTitles.state.setAnimation(0, `bigWinShow`, false);
            this.winTitles.state.addAnimation(0, `bigWinLoop`, false);
            this.winTitles.state.addAnimation(0, `bigWinHide`, false);
            this.winTitles.state.addAnimation(0, `superWinShow`, false);
            this.winTitles.state.addAnimation(0, `superWinLoop`, false);
            this.winTitles.state.addAnimation(0, `superWinHide`, false);
            this.winTitles.state.addAnimation(0, `megaWinShow`, false);
            this.winTitles.state.addAnimation(0, `megaWinLoop`, true);

            this.winLabel.style.fontSize = BIG_WIN_FONT_SIZE;
            await this._showProgressWin(WIN_LAST_DURATION[2]);
            await delay(WIN_REMAIN_DURATION);
            this.winTitles.state.setAnimation(0, `megaWinHide`, false);

            if (this.swordBattle) {
                soundManager.stopSoundInstance(this.swordBattle);
                this.swordBattle = undefined;
            }

            if (this.lionBattle) {
                soundManager.stopSoundInstance(this.lionBattle);
                this.lionBattle = undefined;
            }

            this.particleSystem.stop();
            setTimeout(() => {
                resolve();
            }, 500);
        });
    }

    public onLandscape() {
        this.winTitles.scale.set(0.4);
        this.winTitles.y = 0;
        this.winLabelContainer.y = 50;
    }
    public onPortrait() {
        this.winTitles.scale.set(0.5);
        this.winTitles.y = 0;
        this.winLabelContainer.y = 100;
    }

    private async _showProgressWin(duration: number = 0) {
        return new Promise<void>((resolve) => {
            const startTime = performance.now();
            const startValue = 0;
            const endValue = this.prize;

            const ticker = PIXI.Ticker.shared;

            this.isStopped = false; // Reset the stop flag
            this.winLabel.text = GameEngine.getInstance().getFormatter().format(0);

            const update = () => {
                if (this.isStopped) {
                    // If stopped, show the final value and resolve
                    this.winLabel.text = GameEngine.getInstance()
                        .getFormatter()
                        .format(endValue);
                    ticker.remove(update); // Stop the ticker update
                    resolve(); // Finish the promise
                    return;
                }

                const elapsed = performance.now() - startTime;
                const progress = Math.min(elapsed / duration, 1); // Ensure progress does not exceed 1
                const currentValue = startValue + (endValue - startValue) * progress;
                this.winLabel.text = GameEngine.getInstance()
                    .getFormatter()
                    .format(currentValue);

                if (progress === 1) {
                    this.winLabel.text = GameEngine.getInstance()
                        .getFormatter()
                        .format(currentValue);
                    ticker.remove(update); // Stop the ticker update
                    setTimeout(resolve, 1000); // Resolve after delay
                }
            };

            ticker.add(update);
        });
    }

    public async showPrize(prize: number, odd: number) {
        return new Promise<void>(async (resolve) => {
            this.isStopped = false;
            this.winTitles.visible = false;

            gameStateSoundManager.updateBgMusicVolume(0.2);
            let crowdChantingLoop: SoundInstance | undefined = undefined;
            let coinLoop: SoundInstance | undefined = undefined;

            this.alreadyPlayedWinSounds.clear();

            this.winLayer.visible = true;
            this.winLabelContainer.visible = true;
            this.prize = prize;
            this.odd = odd;
            if (this.odd < 1) {
                this.level = 0;

                this.winSmall = await soundManager.playSound("winSmall");

                await this._showStaticWin();
            } else if (this.odd < 10) {
                this.level = 1;

                this.winNormal = await soundManager.playSound("winNormal");

                await this._showCounterWin();

                await soundManager.playSound("winNormalEnd");
            } else if (this.odd < 20) {
                this.level = 2;

                this.winBig = await soundManager.playSound("winBig");
                coinLoop = await soundManager.playSound("coinLoop");
                console.log("win big played");

                await this._showBigWin();
            } else if (this.odd < 40) {
                this.level = 3;

                if (!this.alreadyPlayedWinSounds.has("winSuper")) {
                    this.winSuper = await soundManager.playSound("winSuper");
                    crowdChantingLoop = await soundManager.playSound("crowdChantingLoop");
                    coinLoop = await soundManager.playSound("coinLoop");
                    this.alreadyPlayedWinSounds.add("winSuper");
                }

                await this._showSuperWin();
            } else {
                this.level = 4;

                if (!this.alreadyPlayedWinSounds.has("winMega")) {
                    this.winMega = await soundManager.playSound("winMega");
                    crowdChantingLoop = await soundManager.playSound("crowdChantingLoop");
                    coinLoop = await soundManager.playSound("coinLoop");
                    this.alreadyPlayedWinSounds.add("winMega");
                }

                await this._showMegaWin();
            }
            this.isStopped = false;

            if (crowdChantingLoop) {
                soundManager.stopSoundInstance(crowdChantingLoop);
                crowdChantingLoop = undefined;
            }

            if (coinLoop) {
                soundManager.stopSoundInstance(coinLoop);
                coinLoop = undefined;
            }

            gameStateSoundManager.updateBgMusicVolume();

            this.winTitles.visible = false;
            this.winLayer.visible = false;
            this.winLabelContainer.visible = false;
            resolve();
        });
    }

    public quickStop() {
        this.isStopped = true;
        switch (this.level) {
            case 0:
                break;
            case 1:

                break;
            case 2:
                break;
            case 3:
                if (this.winTitles.state.getCurrent(0)?.animation?.name.includes("big")) {
                    this.winTitles.state.setAnimation(0, `superWinShow`, false);
                    this.winTitles.state.addAnimation(0, `superWinLoop`, true);
                }
                break;
            case 4:
                if (this.winTitles.state.getCurrent(0)?.animation?.name.includes("big") || this.winTitles.state.getCurrent(0)?.animation?.name.includes("super")) {
                    this.winTitles.state.setAnimation(0, `megaWinShow`, false);
                    this.winTitles.state.addAnimation(0, `megaWinLoop`, true);
                }
                break;
        }
    }
}
